import { capitalizeString } from '@zspace/format';
import {
  DevicePermissions,
  RegistrationFilePermissions,
  userIsManagerOfProvidedSalesOrders,
} from '@zspace/roles';
import {
  RegistrationFilesTableRow,
  RegistrationFileStatus,
} from '@zspace/types';
import { useMemo, useState } from 'react';
import { Icon } from 'react-bulma-components';
import { FaDownload, FaShare } from 'react-icons/fa6';
import CheckPermissions from '../../shared/check-permissions/check-permissions';
import useUser from '../../shared/hooks/user';
import If from '../../shared/if/if';
import { formatDateToLocaleString } from '../../shared/utils';
import Button from '../../ui/button/button';
import Link from '../../ui/link/link';
import PaginatedTable, {
  CustomPaginatedTableProps,
} from '../../ui/table/paginated-table/paginated-table';
import { Column, RowInteraction } from '../../ui/table/types';
import styles from './registration-files-table.module.scss';

export type RegistrationFilesTableProps =
  CustomPaginatedTableProps<RegistrationFilesTableRow> & {
    handleDownloadRegistrationFileClick: (fileName: string) => void;
    isDownloading: boolean;
    handleSendRegistrationFileClick: (fileId: string) => void;
  };

export function RegistrationFilesTable({
  dataSource,
  config,
  form,
  onChange,
  empty,
  handleDownloadRegistrationFileClick,
  isDownloading,
  handleSendRegistrationFileClick,
}: RegistrationFilesTableProps) {
  const { user } = useUser();
  const [activeRowId, setActiveRowId] = useState<string>('');

  const columns: Column<RegistrationFilesTableRow>[] = useMemo(
    () => [
      {
        key: 'name',
        className: 'is-width-30',
        render: (el) => el.name,
        title: 'Registration file',
        sortable: true,
      },
      {
        key: 'createdAt',
        className: 'is-width-10',
        render: (el) => formatDateToLocaleString(el.createdAt),
        title: 'Creation date',
        sortable: true,
      },
      {
        key: 'expiresAt',
        className: 'is-width-10',
        render: (el) => formatDateToLocaleString(el.expiresAt),
        title: 'Expiration date',
        sortable: true,
      },
      {
        key: 'status',
        className: 'is-width-10',
        render: (el) => capitalizeString(el.status),
        title: 'Status',
        sortable: false,
      },
      {
        key: 'linkedDevices',
        className: 'is-width-10',
        render: (el) => (
          <>
            <span>{el.linkedDevices}</span>{' '}
            <CheckPermissions
              permissions={DevicePermissions.REGISTERED_DEVICES_READ}
            >
              <CheckPermissions.Render>
                <If condition={el.createdBy.email === user.email}>
                  <span className="pl-2">
                    <Link
                      className={`is-size-7 ${
                        el.id === activeRowId ? '' : `${styles.invisible}`
                      }`}
                      color="primary"
                      underlined
                      to={`my-devices/device-registration/registration-files/${el.id}`}
                    >
                      Details
                    </Link>
                  </span>
                </If>
              </CheckPermissions.Render>
            </CheckPermissions>
          </>
        ),
        title: 'Linked devices',
        sortable: false,
      },
      {
        key: 'registeredDevices',
        className: 'is-width-15',
        render: (el) => el.registeredDevices,
        title: 'Devices registered with this file',
        sortable: false,
      },
      {
        key: 'actions',
        className: 'is-width-15',
        render: (el: RegistrationFilesTableRow) => {
          const isFileManageable =
            el.status === RegistrationFileStatus.ACTIVE &&
            el.relatedSalesOrders.some((so) =>
              userIsManagerOfProvidedSalesOrders(user, so)
            );

          return (
            <If condition={isFileManageable}>
              <CheckPermissions
                permissions={
                  RegistrationFilePermissions.REGISTRATION_FILES_DOWNLOAD
                }
              >
                <CheckPermissions.Render>
                  <Button
                    ml={1}
                    text
                    className={`has-text-primary-dark ${styles.buttonText} ${
                      el.id === activeRowId ? '' : `${styles.invisible}`
                    }`}
                    onClick={() => handleDownloadRegistrationFileClick(el.id)}
                    isExecutingAction={isDownloading}
                  >
                    <Button.LoadingIcon icon={FaDownload} />
                    <span className="is-underlined">Download</span>
                  </Button>
                </CheckPermissions.Render>
              </CheckPermissions>
              <CheckPermissions
                permissions={
                  RegistrationFilePermissions.REGISTRATION_FILES_SEND
                }
              >
                <CheckPermissions.Render>
                  <Button
                    ml={1}
                    text
                    className={`has-text-primary-dark ${styles.buttonText} ${
                      el.id === activeRowId ? '' : `${styles.invisible}`
                    }`}
                    onClick={() => handleSendRegistrationFileClick(el.id)}
                  >
                    <Icon>
                      <FaShare />
                    </Icon>
                    <span className="is-underlined">Send</span>
                  </Button>
                </CheckPermissions.Render>
              </CheckPermissions>
            </If>
          );
        },
        title: '',
        sortable: false,
      },
    ],
    [
      activeRowId,
      handleDownloadRegistrationFileClick,
      handleSendRegistrationFileClick,
      isDownloading,
      user,
    ]
  );

  const rowInteraction: RowInteraction = {
    onRowEnter: function (rowKey: string): void {
      setActiveRowId(rowKey);
    },
    onRowLeave: function (rowKey: string): void {
      setActiveRowId('');
    },
  };

  return (
    <PaginatedTable
      dataSource={dataSource}
      columns={columns}
      config={config}
      rowKey={'id'}
      empty={empty}
      form={form}
      rowInteraction={rowInteraction}
      onChange={onChange}
    />
  );
}

export default RegistrationFilesTable;
